import React, { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation } from 'react-query'
import { Icon, Input, Label, Stack } from '@crossfit/ui'

import type { Invitee } from 'types'
import * as affiliatesService from 'services/affiliates'
import {
  LoadingResult,
  ErrorResult,
  ValidInvitee,
  InvalidInvitee,
} from './Results'
import style from './style.module.css'
import { SearchForTeammateErrorCode } from 'services/affiliates'
import { useSelector } from 'react-redux'
import { getAffiliate, getMyAffiliates } from 'store/selectors'

enum InvalidReason {
  NeedsCertificate,
  HasPendingInvite,
  IsAlreadyOnTeam,
  IsAnLOR,
  IsAlreadyOnAnotherTeam,
}

function useSearchForm() {
  const [error, setError] = useState('')
  const form = useForm()
  const search = useMutation(affiliatesService.searchForTeammate)
  const { selectedAffiliate: affiliate } = useSelector(getMyAffiliates)
  const { invitations, members: affiliateMembers } = useSelector(getAffiliate)

  useEffect(() => {
    const subscription = form.watch(() => {
      search.reset()
    })

    return () => subscription.unsubscribe()
  }, [form.watch])

  async function handleSubmit({ email }) {
    setError('')

    try {
      await search.mutateAsync(email)
    } catch (error) {
      if (error.response?.status === 404) {
        setError(error.response.data.description)
        return
      }

      setError(error.message)
      throw error
    }
  }

  const invalidReason = useMemo(() => {
    if (!search.data) {
      return
    }
    switch (search.data.errorCode) {
      case SearchForTeammateErrorCode.IS_LOR:
        return InvalidReason.IsAnLOR
      case SearchForTeammateErrorCode.EXPIRED_CERTS:
      case SearchForTeammateErrorCode.NO_CERTS:
        return InvalidReason.NeedsCertificate
      case SearchForTeammateErrorCode.IN_TEAM:
        return InvalidReason.IsAlreadyOnAnotherTeam
    }

    if (affiliateMembers?.some((teammate) => teammate.id === search.data.id)) {
      return InvalidReason.IsAlreadyOnTeam
    }

    if (invitations.some((invitation) => invitation.id === search.data.id)) {
      return InvalidReason.HasPendingInvite
    }
  }, [search.data, invitations, affiliate])

  const invitee = useMemo(() => {
    if (search.data) {
      const { id, firstName, lastName, email, certifications } = search.data
      return {
        id,
        firstName,
        lastName,
        email,
        certifications,
      }
    }
  }, [search.data])

  return {
    ...form,
    onSubmit: form.handleSubmit(handleSubmit),
    invitee,
    isSubmitting: search.isLoading,
    error,
    invalidReason,
  }
}

type Props = {
  onSelect: (invitee: Invitee) => void
}

function SearchForm({ onSelect }: Props) {
  const {
    onSubmit,
    register,
    isSubmitting,
    invitee,
    error,
    invalidReason,
    reset,
  } = useSearchForm()

  function handleClickResults() {
    reset()
    onSelect(invitee)
  }

  return (
    <form onSubmit={onSubmit}>
      <Stack spacing={2} mb={12} style={{ position: 'relative' }}>
        <Label htmlFor='email'>Invite via Email Address</Label>
        <div className={style.inputWrapper}>
          <Input
            required
            type='email'
            id='email'
            placeholder='Search by email'
            disabled={isSubmitting}
            className={style.input}
            {...register('email')}
          />
          <button type='submit' className={style.button} aria-label='Search'>
            <Icon name='search' className={style.icon} />
          </button>
        </div>

        {isSubmitting && <LoadingResult className={style.result} />}

        {error && <ErrorResult className={style.result}>{error}</ErrorResult>}

        {invitee && !invalidReason && (
          <ValidInvitee
            className={style.result}
            onClick={handleClickResults}
            invitee={invitee}
          />
        )}

        {invalidReason === InvalidReason.NeedsCertificate && (
          <InvalidInvitee
            className={style.result}
            invitee={invitee}
            reasonText='Credential Needed'
            reasonStatus='error'
          />
        )}

        {invalidReason === InvalidReason.HasPendingInvite && (
          <InvalidInvitee
            className={style.result}
            invitee={invitee}
            reasonText='This person already has an invite to your Team'
            reasonStatus='notice'
          />
        )}

        {invalidReason === InvalidReason.IsAlreadyOnTeam && (
          <InvalidInvitee
            className={style.result}
            invitee={invitee}
            reasonText='This person is already on
            your Team'
            reasonStatus='notice'
          />
        )}
      </Stack>
    </form>
  )
}

export default SearchForm
